import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useMyData, useCreateCampaign, useDeleteCampaign } from '../hooks/useCampaigns'

export function DashboardPage() {
  const navigate = useNavigate()
  const { data: memberships, isLoading, error } = useMyData()
  const createCampaign = useCreateCampaign()
  const deleteCampaign = useDeleteCampaign()
  const [showNewCampaign, setShowNewCampaign] = useState(false)
  const isGM = memberships?.some(m => m.role === 'gm') ?? false
  const [newName, setNewName] = useState('')
  const [newSetting, setNewSetting] = useState('Kaldor')

  // Players in a single campaign: skip dashboard, go straight to campaign
  useEffect(() => {
    if (!memberships || memberships.length !== 1) return
    if (memberships[0].role !== 'player') return
    const campaign = memberships[0].campaigns as unknown as { id: string } | null
    if (campaign) {
      navigate(`/campaign/${campaign.id}`, { replace: true })
    }
  }, [memberships, navigate])

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim()) return
    await createCampaign.mutateAsync({ name: newName, setting: newSetting })
    setNewName('')
    setShowNewCampaign(false)
  }

  if (isLoading) return <div className="p-8 text-ink-light">Loading...</div>
  if (error) return <div className="p-8 text-danger">Error: {error.message}</div>

  if (!memberships?.length) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <header className="flex justify-between items-center mb-8 border-b border-parchment-dark pb-4">
          <h1 className="text-3xl font-bold text-accent">HârnMaster</h1>
          <button onClick={handleLogout} className="px-4 py-2 text-sm text-ink-light border border-parchment-dark rounded hover:bg-parchment-dark">
            Sign out
          </button>
        </header>
        <p className="text-ink-light mb-4">No campaigns yet.</p>
        {isGM && (
        <button
          onClick={() => setShowNewCampaign(true)}
          className="px-4 py-2 bg-accent text-white rounded hover:bg-accent-light text-sm font-medium"
        >
          + New Campaign
        </button>
        )}
        {isGM && showNewCampaign && (
          <form onSubmit={handleCreate} className="mt-4 p-4 bg-white border border-parchment-dark rounded-lg">
            <div className="flex gap-4 mb-3">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Campaign Name</label>
                <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Kaldor Campaign" required
                  className="w-full px-3 py-2 border border-parchment-dark rounded focus:outline-none focus:ring-2 focus:ring-accent" />
              </div>
              <div className="w-40">
                <label className="block text-sm font-medium mb-1">Setting</label>
                <input value={newSetting} onChange={e => setNewSetting(e.target.value)}
                  className="w-full px-3 py-2 border border-parchment-dark rounded focus:outline-none focus:ring-2 focus:ring-accent" />
              </div>
            </div>
            <button type="submit" disabled={createCampaign.isPending}
              className="px-4 py-2 bg-accent text-white rounded hover:bg-accent-light disabled:opacity-50 text-sm font-medium">
              {createCampaign.isPending ? 'Creating...' : 'Create'}
            </button>
          </form>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <header className="flex justify-between items-center mb-8 border-b border-parchment-dark pb-4">
        <h1 className="text-3xl font-bold text-accent">HârnMaster</h1>
        <button onClick={handleLogout} className="px-4 py-2 text-sm text-ink-light border border-parchment-dark rounded hover:bg-parchment-dark">
          Sign out
        </button>
      </header>

      <section className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">My Campaigns</h2>
          {isGM && (
          <button
            onClick={() => setShowNewCampaign(!showNewCampaign)}
            className="px-4 py-2 bg-accent text-white rounded hover:bg-accent-light text-sm font-medium"
          >
            + New Campaign
          </button>
          )}
        </div>

        {isGM && showNewCampaign && (
          <form onSubmit={handleCreate} className="mb-4 p-4 bg-white border border-parchment-dark rounded-lg">
            <div className="flex gap-4 mb-3">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Campaign Name</label>
                <input
                  value={newName} onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. Kaldor Campaign" required
                  className="w-full px-3 py-2 border border-parchment-dark rounded focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
              <div className="w-40">
                <label className="block text-sm font-medium mb-1">Setting</label>
                <input
                  value={newSetting} onChange={e => setNewSetting(e.target.value)}
                  className="w-full px-3 py-2 border border-parchment-dark rounded focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
            </div>
            <button type="submit" disabled={createCampaign.isPending}
              className="px-4 py-2 bg-accent text-white rounded hover:bg-accent-light disabled:opacity-50 text-sm font-medium">
              {createCampaign.isPending ? 'Creating...' : 'Create'}
            </button>
          </form>
        )}

        {memberships && memberships.length > 0 ? (
          <div className="space-y-2">
            {memberships.map(m => {
              const campaign = m.campaigns as unknown as {
                id: string; name: string; setting: string;
                current_tr_year: number; gm_user_id: string
              }
              if (!campaign) return null
              return (
                <div
                  key={m.id}
                  className="p-4 bg-white border border-parchment-dark rounded-lg flex items-center justify-between hover:border-accent hover:shadow-sm transition-all"
                >
                  <div
                    onClick={() => navigate(`/campaign/${campaign.id}`)}
                    className="flex-1 cursor-pointer"
                  >
                    <strong className="text-accent">{campaign.name}</strong>
                    <span className="ml-3 text-ink-light">
                      {campaign.setting} — TR {campaign.current_tr_year}
                    </span>
                    <span className="ml-3 text-xs text-ink-light uppercase">
                      {m.role}
                    </span>
                  </div>
                  {m.role === 'gm' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        if (confirm(`Delete "${campaign.name}"? This will delete all characters and data in this campaign. This cannot be undone.`)) {
                          deleteCampaign.mutate(campaign.id)
                        }
                      }}
                      className="ml-3 px-2 py-1 text-xs text-danger border border-danger/30 rounded hover:bg-red-50 transition-colors"
                    >
                      Delete
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-ink-light">No campaigns yet. Create one to get started.</p>
        )}
      </section>
    </div>
  )
}
